/**
 * CR-6448: supplier-view layout for the Sales Orders → Forecast filter panel.
 *
 * Renders exactly the controls in the supplied design, grouped into its four
 * sections — Date · Company · Supplier · Forecast Details — with the same
 * section shell (`FilterSection` + divider) and control density
 * (`deliveryFilterStyles`) as the Deliveries tab, the accepted reference for
 * every supplier-view filter rework. Controls the design omits (Classification,
 * SO Number, Supplier Part No) remain on the customer view only.
 *
 * Every control keeps its input type: Record Type stays a select (the design's
 * chips already exist as the results strip above the grid).
 */
import { Calendar, Building2, Truck, FileText } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection } from '../../../filters/FilterSection';
import {
  inputCls, labelCls, dateWrap, dateInputCls, gridCls,
} from '../delivery/deliveryFilterStyles';
import { useFilterVisibility } from '../../../../hooks/useFilterVisibility';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface Option { value: string; label: string }

export interface ForecastFilterSupplierLayoutProps {
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  /** Customer sites the supplier serves (value = customer id). */
  siteOptions: Option[];
  /** Warehouses in scope (value = warehouse code). */
  warehouseOptions: Option[];
  supplierGroupOptions: Option[];
  /** The supplier's own site name — shown locked, as the design specifies. */
  supplierSiteName?: string;
  /** Enter in a text field applies the filters. */
  onApply?: () => void;
}

const RECORD_TYPE_OPTIONS: Option[] = [
  { value: '2', label: 'Open PO only' },
  { value: '1', label: 'Forecast only' },
];

export function ForecastFilterSupplierLayout({
  filters,
  onFilterChange,
  siteOptions,
  warehouseOptions,
  supplierGroupOptions,
  supplierSiteName,
  onApply,
}: ForecastFilterSupplierLayoutProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  const searchableField = (label: string, key: string, options: Option[], placeholder: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <SearchableSelect
        options={options}
        value={filters[key] || ''}
        onChange={(val) => onFilterChange(key, val)}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section */}
      <SectionDivider />

      {/* Date — which import snapshot the grid shows */}
      <FilterSection title="Date" icon={Calendar}>
        <div className={gridCls}>
          <div>
            <label className={labelCls}>Import Date</label>
            <div className={dateWrap}>
              <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="date"
                value={filters['importDate'] || ''}
                onChange={(e) => onFilterChange('importDate', e.target.value)}
                className={dateInputCls}
              />
            </div>
          </div>
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Company */}
      <FilterSection title="Company" icon={Building2}>
        <div className={gridCls}>
          {searchableField('Company Site', 'customerId', siteOptions, 'All Company Sites')}
          {fv.warehouse && (
          <div>
            <label className={labelCls}>Warehouse</label>
            <select
              value={filters['warehouse'] || ''}
              onChange={(e) => onFilterChange('warehouse', e.target.value)}
              className={inputCls}
            >
              <option value="">All Warehouses</option>
              {warehouseOptions.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
          </div>
          )}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Supplier — the site is the caller's own, so it is shown locked */}
      <FilterSection title="Supplier" icon={Truck}>
        <div className={gridCls}>
          {searchableField('Supplier Group', 'supplierGroup', supplierGroupOptions, 'All Supplier Groups')}
          <div>
            <label className={labelCls}>Supplier Site</label>
            <input
              type="text"
              value={supplierSiteName || ''}
              disabled
              title="Locked to your supplier site"
              className={`${inputCls} bg-muted/40 text-muted-foreground cursor-not-allowed`}
            />
            <p className="text-xs text-muted-foreground mt-1 italic">Locked to your supplier site</p>
          </div>
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Forecast Details */}
      <FilterSection title="Forecast Details" icon={FileText}>
        <div className={gridCls}>
          <div>
            <label className={labelCls}>Part Number</label>
            <input
              type="text"
              placeholder="Part Number..."
              value={filters['partNumber'] || ''}
              onChange={(e) => onFilterChange('partNumber', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Record Type</label>
            <select
              value={filters['recordType'] || ''}
              onChange={(e) => onFilterChange('recordType', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              {RECORD_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </FilterSection>
    </div>
  );
}
